import React, { useState, useEffect, useRef } from 'react';
import { Box, InputBase, InputAdornment, Paper, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';

/**
 * Reusable search input component with configurable styling and behavior
 * 
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {string} props.value - Controlled value of the search input
 * @param {function} props.onChange - Callback when search value changes
 * @param {function} props.onSearch - Callback when search is triggered (on enter or after debounce)
 * @param {number} props.debounce - Debounce time in milliseconds (default: 500)
 * @param {string} props.width - Width of the search box (default: 100%)
 * @param {Object} props.sx - Additional Material UI sx styles to apply
 */
const SearchInput = ({ 
  placeholder = "Rechercher...", 
  value = "", 
  onChange,
  onSearch,
  debounce = 500,
  width = '100%',
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [searchTerm, setSearchTerm] = useState(value);
  const mountedRef = useRef(false);
  const onSearchRef = useRef(onSearch);
  const valueRef = useRef(value);

  // Keep latest callbacks/values in refs to avoid re-subscribing effects
  useEffect(() => { onSearchRef.current = onSearch; }, [onSearch]);
  useEffect(() => { valueRef.current = value; }, [value]);
  
  // Handle external value changes
  useEffect(() => {
    // Avoid redundant state updates
    if (value !== searchTerm) {
      setSearchTerm(value);
    }
  }, [value]);
  
  // Debounce search
  useEffect(() => {
    // Skip initial mount to prevent immediate onSearch
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      if (onSearchRef.current) {
        onSearchRef.current(searchTerm);
      }
    }, debounce);
    return () => clearTimeout(timer);
  }, [searchTerm, debounce]);
  
  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    if (onChange) {
      onChange(newValue);
    }
  };
  
  // Handle enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSearchRef.current) {
      onSearchRef.current(searchTerm);
    }
  };

  return (
    <Paper
      component="form"
      sx={{
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        width: width,
        borderRadius: '4px',
        boxShadow: 'none',
        border: `1px solid ${colors.grey[300]}`,
        backgroundColor: "background.paper",
        '&:hover': {
          border: `1px solid ${colors.grey[400]}`,
        },
        ...sx
      }}
      onSubmit={(e) => e.preventDefault()}
      {...rest}
    >
      <InputBase
        fullWidth
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon color="action" sx={{ ml: 1, color: colors.grey[500] }} />
          </InputAdornment>
        }
        sx={{
          color:colors.grey[700],
          px: 1,
          py: 0.5,
          '& .MuiInputBase-input': {
            padding: '8px 0',
          },
        }}
      />
    </Paper>
  );
};

export default SearchInput;
